import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, HeartPulse, BadgeCheck, Smartphone, Lock } from "lucide-react";
import { toast } from "sonner";
import ShiftsLayout from "../../components/shifts/ShiftsLayout";
import SuccessState from "../../components/shifts/SuccessState";
import SMSConsentBlock from "../../components/SMSConsentBlock";
import PageMeta from "../../components/seo/PageMeta";
import { US_STATES, SPECIALTIES, SHIFTS_CTAS } from "../../data/marketplaceData";
import { useAuth } from "../../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClinicianSignup() {
  const { setSession } = useAuth();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", password: "",
    specialty: "RN", license_state: "TX", years_experience: 0,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [agree, setAgree] = useState(false);
  const [smsOptIn, setSmsOptIn] = useState(false); // TCPA: SMS consent

  const update = (k) => (e) => {
    const v = e.target.type === "number" ? Number(e.target.value) : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!agree) { toast.error("Please accept the terms to continue."); return; }
    if (form.password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    setSubmitting(true);
    try {
      // Public ShiftBridge clinician signup → Vercel serverless route (Resend alert + confirmation).
      // Auth session creation happens later from /shiftbridge/login (on Vercel production); preview env keeps
      // the old in-place behaviour because /api/auth/register on FastAPI is still wired.
      const requestId = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `req-${Date.now()}`;
      const r = await fetch(`/api/shiftbridge-signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Idempotency-Key": requestId },
        body: JSON.stringify({
          role: "clinician",
          name: form.name || `${form.firstName || ""} ${form.lastName || ""}`.trim(),
          email: form.email,
          phone: form.phone || null,
          discipline: form.discipline || form.profession || null,
          specialty: form.specialty || null,
          location: form.location || null,
          sms_opt_in: smsOptIn,
          notes: form.notes || null,
          idempotencyKey: requestId,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || data.ok === false) throw new Error("Signup failed");
      toast.success("Signup received!", { description: "We'll email you next steps to start claiming shifts." });
      setDone(true);
    } catch {
      toast.error("We couldn't complete your signup", { description: "Please try again, or text us at (832) 344-1525." });
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <ShiftsLayout>
        <PageMeta title="Clinician account created — TRITAL ShiftBridge™" canonical="https://www.tritalcare.com/shiftbridge/signup/clinician" noIndex />
        <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-20 lg:py-32">
          <SuccessState
            title="You're in. Welcome to TRITAL ShiftBridge™."
            message="Next: upload your license and BLS / ACLS — most clinicians are credential-cleared in under 72 hours and can start claiming shifts the same day they're verified."
            primary={{ label: "Open clinician dashboard", to: "/dashboard" }}
            secondary={{ label: "Browse shifts now", to: SHIFTS_CTAS.browse }}
            testid="clinician-signup-success"
          />
        </section>
      </ShiftsLayout>
    );
  }

  return (
    <ShiftsLayout>
      <PageMeta
        title="Clinician signup — TRITAL ShiftBridge™ by TRITAL Care®"
        description="Create a free clinician account on TRITAL ShiftBridge™. RN, LPN, CNA, allied, therapy. W-2 weekly pay, zero clinician fees, transparent rates."
        canonical="https://www.tritalcare.com/shiftbridge/signup/clinician"
        noIndex
      />
      <section className="max-w-[1100px] mx-auto px-5 lg:px-8 py-14 lg:py-20">
        <Link to="/shifts" className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-slate-500 hover:text-trital-navy mb-8" data-testid="clinician-back">
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.8} /> Back to TRITAL ShiftBridge™
        </Link>

        <div className="grid lg:grid-cols-[1.05fr_1fr] gap-10 items-start">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="rounded-3xl bg-white border border-slate-200 shadow-navy p-7 lg:p-9"
            data-testid="clinician-signup-card"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-trital-medical-blue-soft text-trital-navy">
              <HeartPulse className="w-3.5 h-3.5" strokeWidth={1.8} />
              <span className="overline">Clinician signup</span>
            </div>
            <h1 className="mt-4 font-display font-black text-3xl lg:text-4xl text-slate-900 text-balance">Sign up free. Pick up shifts in 72 hours.</h1>
            <p className="mt-3 text-[14px] text-slate-600">Zero clinician fees. W-2 weekly pay. We do the credentialing — you do the bedside.</p>

            <form onSubmit={submit} className="mt-7 space-y-4" data-testid="clinician-signup-form">
              <Field label="Full name (as on license)" required>
                <input type="text" required value={form.name} onChange={update("name")} placeholder="Sarah Johnson, RN" data-testid="clinician-name" className={inputCls} />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Email" required>
                  <input type="email" required value={form.email} onChange={update("email")} placeholder="sarah.johnson@email.com" data-testid="clinician-email" className={inputCls} />
                </Field>
                <Field label="Phone" required>
                  <input type="tel" required value={form.phone} onChange={update("phone")} placeholder="(832) 555-0192" data-testid="clinician-phone" className={inputCls} />
                </Field>
              </div>

              <div className="h-px bg-slate-100 my-2" />
              <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-slate-500">License & specialty</div>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Primary specialty" required>
                  <select required value={form.specialty} onChange={update("specialty")} data-testid="clinician-specialty" className={inputCls}>
                    {SPECIALTIES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="License state" required>
                  <select required value={form.license_state} onChange={update("license_state")} data-testid="clinician-license-state" className={inputCls}>
                    {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Years of experience">
                <input type="number" min="0" max="50" value={form.years_experience} onChange={update("years_experience")} data-testid="clinician-years" className={inputCls} />
              </Field>

              <div className="h-px bg-slate-100 my-2" />

              <Field label="Choose a password (min 8 chars)" required>
                <input type="password" required minLength={8} value={form.password} onChange={update("password")} placeholder="••••••••" data-testid="clinician-password" className={inputCls} />
              </Field>

              {/* TCPA: SMS Consent Block — ALWAYS visible (SimpleTexting requirement) */}
              <div className="my-4">
                <SMSConsentBlock
                  checked={smsOptIn}
                  onCheckedChange={setSmsOptIn}
                  checkboxId="clinician-signup-sms-consent"
                />
              </div>

              <label className="flex items-start gap-2.5 text-[12.5px] text-slate-600 leading-relaxed">
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 rounded border-slate-300 text-trital-navy focus:ring-trital-navy/20" data-testid="clinician-agree" />
                <span>I agree to the <Link to="/terms" className="text-trital-navy hover:underline">terms</Link> and <Link to="/privacy-policy" className="text-trital-navy hover:underline">privacy policy</Link>. I authorize TRITAL Care® to verify my license and credentials.</span>
              </label>

              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-trital-red text-white text-[14px] font-semibold btn-lift hover:bg-trital-red-deep disabled:opacity-70 disabled:cursor-not-allowed"
                data-testid="clinician-submit"
              >
                {submitting ? "Creating account…" : <>Create clinician account <ArrowRight className="w-4 h-4" strokeWidth={1.8} /></>}
              </button>
              <div className="text-[12px] text-slate-500 text-center">
                Already have an account? <Link to={SHIFTS_CTAS.login} className="font-semibold text-trital-navy hover:underline">Sign in</Link>
              </div>
            </form>
          </motion.div>

          {/* Reassurance side panel */}
          <aside className="space-y-3">
            {[
              { Icon: BadgeCheck, t: "Free, always — for clinicians", b: "Zero marketplace fees. The hourly rate posted is what you earn. TRITAL Care is paid by the facility, not by you." },
              { Icon: Smartphone, t: "Mobile-first claim flow",       b: "Upload your license with the camera. Claim shifts in 2 taps. Message the unit lead in-app the day before your shift." },
              { Icon: Lock,       t: "Your data, locked down",        b: "SOC 2 controls. Your license and credentials are encrypted and only shared with the facility you claim a shift at." },
            ].map((c) => (
              <div key={c.t} className="rounded-2xl bg-slate-50 border border-slate-200 p-5">
                <c.Icon className="w-5 h-5 text-trital-red" strokeWidth={1.8} />
                <div className="mt-3 font-display font-bold text-[15px] text-slate-900">{c.t}</div>
                <p className="mt-1.5 text-[13px] text-slate-600 leading-relaxed">{c.b}</p>
              </div>
            ))}
          </aside>
        </div>
      </section>
    </ShiftsLayout>
  );
}

const inputCls = "w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-[14px] focus:outline-none focus:border-trital-navy focus:ring-2 focus:ring-trital-navy/10 transition";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-[12px] font-semibold text-slate-700 mb-1.5 inline-block">
        {label} {required && <span className="text-trital-red">*</span>}
      </span>
      {children}
    </label>
  );
}
